import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SeoService } from 'src/app/seo.service';
import { CommonService } from 'src/app/services/common.service';
import { UiSpinnerService } from 'src/app/services/ui-spinner.service';
import { UnsubscribeOnDestroyAdapter } from 'src/app/shared/UnsubscribeOnDestroyAdapter';
import { Distributor } from 'src/app/superadmin/superadmin.model';
import { httpRoutes, httpVersion } from 'src/consts/http-routes';
import { localStorageConstants } from 'src/consts/local-storage-constants';
import { WEB_ROUTES } from 'src/consts/routes';
import { showNotification } from 'src/consts/utils';

export interface HelpFile {
  module_name: string;
  help_file_url: string;
  _id: string;
  icon: string;
  title?: string;
}

@Component({
  selector: 'app-help-page',
  templateUrl: './help-page.component.html',
  styleUrls: ['./help-page.component.scss'],
  standalone: false
})
export class HelpPageComponent extends UnsubscribeOnDestroyAdapter {
  id = '';
  contact_support_form!: UntypedFormGroup;
  variableDistributorList: Array<Distributor> = [];
  distributorList: Array<Distributor> = this.variableDistributorList.slice();
  helpDetailCards: Array<HelpFile> = [];
  // Project list variable - For Project dropdown, passed this value to the reports Dialog
  variablesProjectList: any = [];
  projectList: any = this.variablesProjectList.slice();

  helpFileMap: { [key: string]: string } = {};


  // Constructor class
  constructor(private seo: SeoService, public dialog: MatDialog, private snackBar: MatSnackBar, public route: ActivatedRoute,
    private router: Router, private commonService: CommonService, public translate: TranslateService,
    public uiSpinner: UiSpinnerService, private fb: UntypedFormBuilder) {
    super();


    this.seo.setTags({
      title: 'Help page Form', // Title
      titleSuffix: '- SmartAccuPay', // Title Suffix
      description: 'SmartAccuPay invoice management', // Description
      image: '', // Image
      keywords: 'SmartAccuPay' // Keywords
    });

    this.contact_support_form = this.fb.group({
      help_email: ['', [Validators.required, Validators.email]],
      help_phone: ['', [Validators.required]],
      help_subject: ['', [Validators.required]],
      help_message: ['', [Validators.required]],
    });


    // Subscribe to value changes and update the first character

    this.contact_support_form.get('help_subject')?.valueChanges.subscribe(value => {
      if (value) {
        this.contact_support_form.patchValue({
          help_subject: value.charAt(0).toUpperCase() + value.slice(1)
        }, { emitEvent: false });
      }
    });

    this.contact_support_form.get('help_message')?.valueChanges.subscribe(value => {
      if (value) {
        this.contact_support_form.patchValue({
          help_message: value.charAt(0).toUpperCase() + value.slice(1)
        }, { emitEvent: false });
      }
    });

    this.translate.stream([""]).subscribe(() => {
      this.loadHelpData()
      // Light Header
      if (localStorage.getItem(localStorageConstants.LANGUAGE) == 'es') {
        // Spanish Header

      } else {
        // English Header

      }


    });


  }




  ngOnInit(): void {
    this.loadHelpData()
  }




  async loadHelpData() {
    const helpData = await this.commonService.getRequestAPI(httpVersion.PORTAL_V1 + httpRoutes.GET_ALL_HELP_CARDS);
    localStorage.setItem(localStorageConstants.HELP_FILE_URL, JSON.stringify(helpData));

    const helpFileUrl = localStorage.getItem(localStorageConstants.HELP_FILE_URL) ?? '';

    if (helpFileUrl) {
      const response = JSON.parse(helpFileUrl);
      if (response && response.data && response.data.length > 0) {
        this.helpDetailCards = response.data[0].helpfiles.map((file: any, index: number) => ({
          ...file,
          title: this.translate.instant(file.module_name),  // Translate the title
          icon: this.getIconClassForModule(index)  // Get the appropriate icon class
        }));
      }
    }
  }




  getIconClassForModule(index: number): string {
    const icons = [
      'fas fa-truck-moving bg-red sell-icon',       //change order
      'fas fa-user bg-blue sell-icon',        //clients
      'fas fa-globe bg-green sell-icon',      //Global document
      'fas fa-users bg-yellow sell-icon',     // vendors
      'fas fa-user-circle bg-blue sell-icon',    //purchase order
      'fas fa-receipt bg-red sell-icon',    // items
      'fas fa-door-closed bg-cyan sell-icon',          //Invoice
      'fas fa-dollar-sign bg-green sell-icon',      //Expense
      'fas fa-file-invoice bg-yellow sell-icon',       //Estimate
      'fas fa-cog bg-purple sell-icon',     //Safety talk
      'fas fa-list-alt bg-orange sell-icon',         //Safety checklist
      'fas fa-sign-in-alt bg-pink sell-icon',         //Login
      'fas fa-key bg-teal sell-icon',                 //Forgot password
      'fas fa-user-lock  bg-brown sell-icon',         //Forcefully password change
      'fas fa-lock bg-green sell-icon',         //change passowd
      'fas fa-envelope-open-text  bg-red sell-icon',         //email otp login
      'fas fa-list-alt bg-orange sell-icon',         //checklist
      'fas fa-chart-bar bg-indigo sell-icon',         //daily report
      'fas fa-tachometer-alt bg-pink sell-icon',         //dashboard
      'fas fa-archive bg-deep-orange sell-icon',         //extra material
      'fas fa-map-marker-alt bg-green sell-icon',         //location
      'fas fa-boxes bg-blue sell-icon',         //material used
      'fas fa-comment-dots bg-orange sell-icon',         //messaging
      'fas fa-money-check-alt bg-purple sell-icon',         //payroll
      'fas fa-clock bg-orange sell-icon',         //shift
      'fas fa-layer-group bg-cyan sell-icon',         //blueprint
      'fas fa-tasks bg-teal sell-icon',              //Task
      'fas fa-cogs bg-purple sell-icon',             //Project setting
      'fas fa-user bg-red sell-icon',                        // project
      'fas fa-calendar-alt bg-blue sell-icon'                   // Schedule of value 

    ];
    return icons[index % icons.length];
  }


  openHelpFile(url: string): void {
    window.open(url, '_blank');
  }


  async sendButtonAction() {
    if (this.contact_support_form.valid) {
      this.uiSpinner.spin$.next(true);
      this.commonService.postRequestWithoutTokenAPI(httpVersion.PORTAL_V1 + httpRoutes.SEND_HELP, this.contact_support_form.value).subscribe((data) => {
        if (data.status) {
          this.uiSpinner.spin$.next(false);
          showNotification(this.snackBar, 'Help request to support team sent successfully', 'success');
          this.contact_support_form.reset();
        }
        else {
          this.uiSpinner.spin$.next(false);

          showNotification(this.snackBar, data.message, 'error');
        }
      });



    }
  }

  // Back action to navigate to previous page
  back() {
    this.router.navigate([WEB_ROUTES.SIDEMENU_DASHBOARD]);
  }


}
